import React from "react";
import { cn } from "@/lib/utils";
import { Info } from "lucide-react";

const TITLE_SIZE = { sm: "text-xs", md: "text-sm", lg: "text-base" };
const TITLE_ALIGN = { left: "text-left", center: "text-center", right: "text-right" };
const WEIGHTS = { normal: 400, medium: 500, semibold: 600, bold: 700 };
const SHADOWS = {
  none: "none",
  sm: "0 4px 14px rgba(10,16,41,0.08)",
  md: "0 10px 30px rgba(10,16,41,0.14)",
  lg: "0 18px 60px rgba(10,16,41,0.22)",
};

function hexToRgba(hex, alpha) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return hex;
  return `rgba(${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}, ${alpha})`;
}

function titleInlineStyle(ts) {
  const st = {};
  if (ts.color) st.color = ts.color;
  if (ts.fontFamily) st.fontFamily = ts.fontFamily;
  if (ts.sizePx) st.fontSize = Number(ts.sizePx);
  if (ts.weight && WEIGHTS[ts.weight]) st.fontWeight = WEIGHTS[ts.weight];
  if (ts.bold) st.fontWeight = 700; // Bold toggle always wins (item 38 fix)
  if (ts.italic) st.fontStyle = "italic";
  if (ts.underline) st.textDecoration = "underline";
  return Object.keys(st).length ? st : undefined;
}

export function WidgetCard({ title, subtitle, actions, selected, children, className, onClick, onMouseDown, testid, titleStyle, subtitleStyle, description, hideHeader, ariaLabel, appearance, widgetType, headerDrag }) {
  const ts = titleStyle || {};
  const ss = subtitleStyle || {};
  const showText = !hideHeader && (title || subtitle);
  const showHeader = showText || actions;

  const custom = !!(ap0(appearance).transparent || ap0(appearance).bg_color || ap0(appearance).bg_image || ap0(appearance).border_color || ap0(appearance).border_width != null || ap0(appearance).radius != null || (ap0(appearance).shadow && ap0(appearance).shadow !== "default"));
  const ap = ap0(appearance);
  const style = {};
  if (ap.transparent) style.background = "transparent";
  else if (ap.bg_color) style.background = (ap.bg_opacity ?? 1) < 1 ? hexToRgba(ap.bg_color, ap.bg_opacity) : ap.bg_color;
  if (ap.border_color || ap.border_width != null) style.border = `${ap.border_width ?? 1}px solid ${ap.border_color || "var(--glass-border)"}`;
  if (ap.radius != null) style.borderRadius = ap.radius;
  if (ap.shadow && ap.shadow !== "default") style.boxShadow = SHADOWS[ap.shadow];
  if (custom && !ap.transparent && !ap.bg_color) style.background = "var(--glass-bg)";
  if (custom && style.borderRadius == null) style.borderRadius = 16;
  if (custom && !style.border) style.border = "1px solid var(--glass-border)";

  const headerStyle = {};
  if (ts.bg) headerStyle.background = ts.bg;
  if (ts.padding != null && ts.padding !== "") headerStyle.padding = `${Number(ts.padding)}px 16px`;

  const subStyle = {};
  if (ss.color) subStyle.color = ss.color;
  if (ss.fontFamily) subStyle.fontFamily = ss.fontFamily;
  if (ss.sizePx) subStyle.fontSize = Number(ss.sizePx);
  if (ss.italic) subStyle.fontStyle = "italic";

  return (
    <div
      data-testid={testid}
      data-widget-type={widgetType || undefined}
      onClick={onClick}
      onMouseDown={onMouseDown}
      role="figure"
      aria-label={ariaLabel || title || undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter" && e.target === e.currentTarget) onClick(e); } : undefined}
      style={style}
      className={cn(
        !custom && "glass-card",
        "relative h-full flex flex-col overflow-hidden transition-shadow duration-200 widget-rise hover:shadow-[var(--shadow-md)]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--brand-ocean)]",
        selected && "ring-2 ring-[color:var(--brand-ocean)] ring-offset-2 ring-offset-[color:var(--canvas)]",
        className
      )}
    >
      {ap.bg_image && (
        <img src={ap.bg_image} alt="" aria-hidden
          className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
          style={{ opacity: ap.bg_image_opacity ?? 0.25, borderRadius: "inherit" }} />
      )}
      {showHeader && (
        <div data-testid={headerDrag ? "widget-header-drag" : undefined}
          className={cn("relative flex items-start justify-between gap-2 px-4 pt-3.5 pb-2", headerDrag && "widget-header-drag cursor-move", typeof headerDrag === "string" && headerDrag)} style={Object.keys(headerStyle).length ? headerStyle : undefined}>
          <div className={cn("min-w-0 flex-1", TITLE_ALIGN[ts.align || "left"])}>
            {title && !hideHeader && (
              <div className={cn("flex items-center gap-1.5 min-w-0", ts.align === "center" && "justify-center", ts.align === "right" && "justify-end")}>
                <span style={titleInlineStyle(ts)}
                  className={cn(!ts.sizePx && TITLE_SIZE[ts.size || "md"], !ts.weight && !ts.bold && "font-medium", !ts.color && "text-[color:var(--ink-2)]", "truncate")}>
                  {title}
                </span>
                {description && (
                  <span data-testid="widget-desc-icon" title={description} aria-label={description}
                    className="shrink-0 inline-flex text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)] cursor-help">
                    <Info className="w-3.5 h-3.5" />
                  </span>
                )}
              </div>
            )}
            {subtitle && !hideHeader && (
              <div style={Object.keys(subStyle).length ? subStyle : undefined}
                className={cn(!ss.sizePx && "text-xs", !ss.color && "text-[color:var(--ink-3)]", "truncate")}>{subtitle}</div>
            )}
          </div>
          {actions && <div className="flex items-center gap-1 flex-wrap justify-end min-w-0">{actions}</div>}
        </div>
      )}
      {!showHeader && description && (
        <span data-testid="widget-desc-icon" title={description} aria-label={description}
          className="absolute top-2 left-2 z-10 inline-flex text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)] cursor-help opacity-60 hover:opacity-100">
          <Info className="w-3.5 h-3.5" />
        </span>
      )}
      <div className="relative flex-1 min-h-0 px-3 pb-3" style={ap.padding != null ? { padding: ap.padding } : undefined}>{children}</div>
    </div>
  );
}

function ap0(a) { return a || {}; }
